// WarrantyButton — Overview-tab CTA for opening warranty claims.
//
// Gated visibility: stage = 'closed_won' AND warranty_registered_date != null.
// Hidden otherwise. When no active claims exist, button reads "+ Open warranty
// claim". When 1+ active claims exist, reads "Warranty · N active" with amber
// pulsing dot — click switches to the deal's Warranty tab.
//
// Active = status IN ('claim_filed', 'in_progress'). Terminal statuses
// (claim_approved/claim_denied/voided/expired) are NOT counted.

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import OpenWarrantyClaimModal from "./OpenWarrantyClaimModal";

interface DealForButton {
  id: string;
  homeowner_name: string | null;
  property_street: string | null;
  property_city: string | null;
  owner_id: string | null;
  stage: string | null;
  warranty_registered_date: string | null;
}

interface Props {
  deal: DealForButton;
  reload: () => Promise<void> | void;
}

export default function WarrantyButton({ deal, reload }: Props) {
  const [, setSearchParams] = useSearchParams();
  const [activeCount, setActiveCount] = useState<number>(0);
  const [open, setOpen] = useState(false);

  const eligible = deal.stage === "closed_won" && !!deal.warranty_registered_date;

  useEffect(() => {
    let cancelled = false;
    if (!eligible) {
      setActiveCount(0);
      return;
    }
    (async () => {
      const { count } = await supabase
        .from("warranty_claims")
        .select("id", { count: "exact", head: true })
        .eq("deal_id", deal.id)
        .in("status", ["claim_filed", "in_progress"]);
      if (!cancelled) setActiveCount(count ?? 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [deal.id, eligible]);

  // Realtime — keep count fresh while the user is on the deal
  useEffect(() => {
    if (!eligible) return;
    const channel = supabase
      .channel(`warranty_button_${deal.id}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "warranty_claims", filter: `deal_id=eq.${deal.id}` },
        async () => {
          const { count } = await supabase
            .from("warranty_claims")
            .select("id", { count: "exact", head: true })
            .eq("deal_id", deal.id)
            .in("status", ["claim_filed", "in_progress"]);
          setActiveCount(count ?? 0);
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [deal.id, eligible]);

  if (!eligible) return null;

  const hasActive = activeCount > 0;

  function handleClick() {
    if (hasActive) {
      // Switch to the Warranty tab (URL-backed via useTabParam pattern).
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", "warranty");
        return next;
      }, { replace: true });
    } else {
      setOpen(true);
    }
  }

  return (
    <>
      <button
        onClick={handleClick}
        className={
          "px-3 py-1.5 text-xs font-medium rounded inline-flex items-center gap-1.5 transition " +
          (hasActive
            ? "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
            : "bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200")
        }
      >
        {hasActive && (
          <span className="relative inline-flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-70"></span>
            <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500"></span>
          </span>
        )}
        {hasActive ? `Warranty · ${activeCount} active` : "+ Open warranty claim"}
      </button>
      {open && (
        <OpenWarrantyClaimModal
          deal={deal}
          onClose={() => setOpen(false)}
          onCreated={async () => {
            setOpen(false);
            // Switch to warranty tab so the user lands on the new claim.
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set("tab", "warranty");
              return next;
            }, { replace: true });
            await reload();
          }}
        />
      )}
    </>
  );
}
